/**
 * The composer's placeholder and its rotating examples, by the page the chat is docked beside
 * (MAR-948, out of `ChatPanel.vue`). Laws and sheets get their own; everything else the general set.
 * Keys are written out in full so an i18n scan can find each one.
 */
import { computed, type Ref } from 'vue'

type Translate = (key: string) => string
type Area = 'laws' | 'sheets' | 'general'

const PLACEHOLDER: Record<Area, string> = {
  laws: 'webChat.placeholderLaws',
  sheets: 'webChat.placeholderSheets',
  general: 'webChat.placeholder',
}

const ROTATING: Record<Area, string[]> = {
  laws: [
    'webChat.chatPlaceholders.lawsCommerce',
    'webChat.chatPlaceholders.lawsLisr',
    'webChat.chatPlaceholders.lawsJuris',
    'webChat.chatPlaceholders.lawsLabor',
  ],
  sheets: [
    'webChat.chatPlaceholders.sheetsAsk',
    'webChat.chatPlaceholders.sheetsSummarize',
    'webChat.chatPlaceholders.sheetsAddCol',
    'webChat.chatPlaceholders.sheetsFilter',
  ],
  general: [
    'webChat.chatPlaceholders.generalSearch',
    'webChat.chatPlaceholders.generalLaw',
    'webChat.chatPlaceholders.generalDraft',
    'webChat.chatPlaceholders.generalSummarize',
    'webChat.chatPlaceholders.generalCompare',
    'webChat.chatPlaceholders.generalExtract',
  ],
}

function areaOf(path: string): Area {
  if (path.startsWith('/app/laws')) return 'laws'
  if (path.startsWith('/app/sheets')) return 'sheets'
  return 'general'
}

export function useChatPlaceholders(path: Ref<string>, t: Translate) {
  const area = computed(() => areaOf(path.value))
  const placeholder = computed(() => t(PLACEHOLDER[area.value]))
  const placeholders = computed(() => ROTATING[area.value].map(key => t(key)))
  return { placeholder, placeholders }
}
